import { useEffect, useId, useState, type ComponentProps, type ReactNode } from 'react'
import { Ban, CalendarClock, Check, CircleDot, Flag, Minus, UserRound, X } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field'
import { FormDialog, FormDialogContent, FormDialogTrigger } from '@/components/ui/form-dialog'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { apiClient } from '@/lib/api/client'
import type {
  BatchInquiryItem,
  Consignment,
  HelmInquiryPriority,
  HelmUser,
  Inquiry,
} from '@/lib/api/types'
import {
  BATCH_LIMIT,
  dueDateEntries,
  ownerEntries,
  priorityEntries,
  sharedStatusChoices,
  statusEntries,
  type BatchEntry,
} from '@/lib/batch'
import {
  CONSIGNMENT_STATUSES,
  INQUIRY_STATUSES,
  canCancelInquiry,
  consignmentQuickPatch,
  consignmentStatusChoices,
  dueDateInputValue,
  inquiryQuickPatch,
  inquiryStatusChoices,
  quickEditedFields,
  type StatusChoice,
} from '@/lib/quick-edit'
import { statusLabel } from '@/lib/status-tone'
import { cn } from '@/lib/utils'
import { CancelInquiryDialog } from '@/src/components/CancelInquiryDialog'
import { isAssignableOwner } from '@/src/components/HelmOwnerCombobox'
import { personName } from '@/src/hooks/useHelmPeople'
import type { OptimisticRows } from '@/src/hooks/useOptimisticRows'
import type { RowSelection } from '@/src/hooks/useRowSelection'
import { inquiryTitle } from '@/src/pages/inquirySummary'
import { consignmentLabel } from '@/src/components/QuickEdit'

/**
 * Changing many rows of a list at once, as Linear does: a checkbox on each
 * row, and a bar along the bottom while any are picked. Each action keeps
 * quick edit's rules, and goes to Helm as one batch that changes each record
 * on its own.
 */

/** A row's checkbox, or the header's for every row shown. Shift-click picks a range. */
export function SelectCheckbox({
  checked,
  label,
  onToggle,
}: {
  checked: boolean | 'mixed'
  label: string
  onToggle: (range: boolean) => void
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={(event) => {
        // Picking a row never opens or expands it.
        event.stopPropagation()
        onToggle(event.shiftKey)
      }}
      className={cn(
        // The box is small; the space around it takes the tap.
        "relative flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input bg-background text-primary-foreground outline-none after:absolute after:-inset-2.5 after:content-[''] focus-visible:ring-[3px] focus-visible:ring-ring/50",
        checked !== false && 'border-primary bg-primary',
      )}
    >
      {checked === 'mixed' ? (
        <Minus aria-hidden className="size-3" />
      ) : checked ? (
        <Check aria-hidden className="size-3" />
      ) : null}
    </button>
  )
}

/** The header's checkbox: every row on this page, or none of them. */
export function SelectAllCheckbox<T extends { id: string }>({
  selection,
  visible,
  noun,
}: {
  selection: RowSelection<T>
  visible: readonly T[]
  noun: string
}) {
  const picked = visible.filter((row) => selection.isSelected(row.id)).length
  const checked = picked === 0 ? false : picked === visible.length ? true : 'mixed'
  return (
    <SelectCheckbox
      checked={checked}
      label={`Select every ${noun} shown`}
      onToggle={() => selection.setAll(visible, checked !== true)}
    />
  )
}

/** What selection mode needs, whatever the rows are. */
type SelectionMode = Pick<RowSelection<{ id: string }>, 'selecting' | 'setSelecting' | 'clear'>

/** The checkbox column: on a phone, only in selection mode. */
export function selectColumnClass(selection: Pick<SelectionMode, 'selecting'>) {
  return cn('w-10 pr-0 pl-4', !selection.selecting && 'max-sm:hidden')
}

/** A phone has no room for checkboxes until asked: Select shows them, Done puts them away. */
export function SelectModeButton({ selection }: { selection: SelectionMode }) {
  return (
    <Button
      type="button"
      variant="outline"
      className="sm:hidden"
      aria-pressed={selection.selecting}
      onClick={() => (selection.selecting ? selection.clear() : selection.setSelecting(true))}
    >
      {selection.selecting ? 'Done' : 'Select'}
    </Button>
  )
}

/** The selected rows as the list shows them now, edits included. */
export function selectedRows<T extends { id: string }>(
  selection: RowSelection<T>,
  visible: readonly T[],
) {
  const shown = new Map(visible.map((row) => [row.id, row]))
  return selection.rows.map((row) => shown.get(row.id) ?? row)
}

/**
 * The bottom of the page while rows are picked: what the last batch did,
 * then the bar. On a phone the bar docks at the bottom edge; the page leaves
 * room under its rows for it.
 */
function BatchDock({
  edits,
  count,
  noun,
  onClear,
  children,
}: {
  edits: Pick<OptimisticRows<{ id: string }>, 'report' | 'dismissReport'>
  count: number
  noun: string
  onClear: () => void
  children: ReactNode
}) {
  const { report, dismissReport } = edits
  const quiet = report && !report.refused.length && !report.detail
  useEffect(() => {
    // A batch that all went through says so, then gets out of the way.
    if (!quiet) return
    const timer = window.setTimeout(dismissReport, 6000)
    return () => window.clearTimeout(timer)
  }, [quiet, report, dismissReport])

  if (!count && !report) return null
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex flex-col items-center gap-2 md:bottom-6">
      {report && (
        <div className="pointer-events-auto w-full px-3 md:w-[30rem] md:px-0">
          <Alert
            variant={quiet ? 'default' : 'destructive'}
            className="max-h-[40dvh] overflow-y-auto pr-12 shadow-lg"
          >
            <AlertTitle>{report.summary}</AlertTitle>
            {(report.detail || report.refused.length > 0) && (
              <AlertDescription>
                {report.detail && <p>{report.detail}</p>}
                {report.refused.length > 0 && (
                  <>
                    <ul className="list-disc space-y-1 pl-4">
                      {report.refused.map((entry) => (
                        <li key={entry.id}>
                          <span className="font-medium">{entry.label}</span>: {entry.message}
                        </li>
                      ))}
                    </ul>
                    <p>They are still selected, to try again.</p>
                  </>
                )}
              </AlertDescription>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Dismiss"
              className="absolute top-2 right-2"
              onClick={dismissReport}
            >
              <X />
            </Button>
          </Alert>
        </div>
      )}
      {count > 0 && (
        <div
          role="region"
          aria-label="Batch actions"
          className="pointer-events-auto w-full border-t bg-popover pb-[env(safe-area-inset-bottom)] text-popover-foreground shadow-lg md:w-auto md:max-w-[calc(100vw-2rem)] md:rounded-full md:border md:pb-0"
        >
          <div className="flex flex-wrap items-center gap-1 p-2 md:flex-nowrap md:py-1.5">
            <span className="px-2 text-sm font-medium whitespace-nowrap tabular-nums">
              {count} selected
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto md:order-last md:ml-0"
              title="Clear the selection (Esc)"
              onClick={onClear}
            >
              Clear
            </Button>
            {/* A phone wraps the actions rather than hiding some off the edge. */}
            <div className="flex w-full flex-wrap items-center gap-1 md:w-auto md:flex-nowrap">
              {children}
            </div>
          </div>
          {count > BATCH_LIMIT && (
            <p className="px-4 pb-2 text-xs text-muted-foreground">
              Up to {BATCH_LIMIT} {noun} at a time; select fewer to change them.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function BarButton({
  icon,
  children,
  ...props
}: ComponentProps<typeof Button> & { icon: ReactNode }) {
  return (
    <Button type="button" variant="ghost" size="sm" {...props}>
      {icon}
      {children}
    </Button>
  )
}

type Row = { id: string; status: string | null; owner_user_id?: string | null }

/** What every selected row has in common, if they all have the same. */
function shared<T, V>(rows: readonly T[], value: (row: T) => V): V | undefined {
  const first = rows[0]
  if (first === undefined) return undefined
  const v = value(first)
  return rows.every((row) => value(row) === v) ? v : undefined
}

/**
 * Status, offering only the moves every selected row can make; a move some
 * cannot make is shown greyed, with where they are stuck.
 */
function StatusMenu<T extends Row>({
  rows,
  choicesFor,
  order,
  noun,
  disabled,
  onPick,
}: {
  rows: readonly T[]
  choicesFor: (row: T) => StatusChoice[]
  order: readonly string[]
  noun: string
  disabled: boolean
  onPick: (status: string) => void
}) {
  const statuses = sharedStatusChoices(rows, choicesFor, order)
  const byId = new Map(rows.map((row) => [row.id, row]))
  const current = shared(rows, (row) => row.status)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <BarButton icon={<CircleDot />} disabled={disabled}>
          Status
        </BarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-72">
        {statuses.map(({ status, available, unavailableFor }) => {
          const stuck = unavailableFor.map((id) => byId.get(id)!)
          const from = [...new Set(stuck.map((row) => statusLabel(row.status)))].join(', ')
          return (
            <DropdownMenuItem key={status} disabled={!available} onSelect={() => onPick(status)}>
              <span className="min-w-0 flex-1">
                {statusLabel(status)}
                {!available && (
                  <span className="block text-xs text-muted-foreground">
                    {stuck.length} selected can't move there from {from}
                  </span>
                )}
              </span>
              {status === current && <Check aria-label="Current" />}
            </DropdownMenuItem>
          )
        })}
        {statuses.some((status) => !status.available) && (
          <>
            <DropdownMenuSeparator />
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              Only the moves Helm allows for every selected {noun} can be picked. Select fewer to
              move the others.
            </p>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Owner: anyone on the staff, or nobody. */
function OwnerMenu<T extends Row>({
  rows,
  people,
  meId,
  disabled,
  onPick,
}: {
  rows: readonly T[]
  people: ReadonlyMap<string, HelmUser>
  meId: string | null
  disabled: boolean
  onPick: (ownerUserId: string) => void
}) {
  const staff = [...people.values()]
    .filter(isAssignableOwner)
    .sort((a, b) => a.email.localeCompare(b.email))
  const current = shared(rows, (row) => row.owner_user_id ?? '')
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <BarButton icon={<UserRound />} disabled={disabled}>
          Owner
        </BarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-64">
        <DropdownMenuItem onSelect={() => onPick('')}>
          <span className="flex-1">Unassigned</span>
          {current === '' && <Check aria-label="Current" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {staff.map((user) => (
          <DropdownMenuItem key={user.id} onSelect={() => onPick(user.id)}>
            <span className="min-w-0 flex-1">
              <span className="block truncate">{user.id === meId ? 'You' : personName(user)}</span>
              <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
            </span>
            {current === user.id && <Check aria-label="Current" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Due date: one day for all, each keeping its own time of day; or none. */
function DueDateAction<T extends Row & { sla_due_at?: string | null }>({
  rows,
  noun,
  disabled,
  onPick,
}: {
  rows: readonly T[]
  noun: string
  disabled: boolean
  onPick: (date: string) => void
}) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState('')

  function openChange(next: boolean) {
    if (next) setDate(shared(rows, (row) => dueDateInputValue(row.sla_due_at)) ?? '')
    setOpen(next)
  }

  function pick(value: string) {
    setOpen(false)
    onPick(value)
  }

  return (
    <FormDialog open={open} onOpenChange={openChange}>
      <FormDialogTrigger asChild>
        <BarButton icon={<CalendarClock />} disabled={disabled}>
          Due date
        </BarButton>
      </FormDialogTrigger>
      <FormDialogContent
        size="sm"
        title="Due date"
        description={`For the ${rows.length} selected ${noun}`}
        submitLabel="Set due date"
        onSubmit={(event) => {
          event.preventDefault()
          if (date) pick(date)
        }}
      >
        <Field>
          <FieldLabel htmlFor={`${id}-due`}>Due on</FieldLabel>
          <Input
            id={`${id}-due`}
            type="date"
            required
            value={date}
            onChange={(event) => setDate(event.target.value)}
            aria-describedby={`${id}-due-help`}
          />
          <FieldDescription id={`${id}-due-help`}>
            Each is due by 17:00 Kampala time that day, unless it already has a time.
          </FieldDescription>
        </Field>
        <Button type="button" variant="outline" className="self-start" onClick={() => pick('')}>
          Clear their due dates
        </Button>
      </FormDialogContent>
    </FormDialog>
  )
}

const PRIORITIES: HelmInquiryPriority[] = ['URGENT', 'HIGH', 'NORMAL', 'LOW']

function PriorityMenu({
  rows,
  disabled,
  onPick,
}: {
  rows: readonly Inquiry[]
  disabled: boolean
  onPick: (priority: HelmInquiryPriority) => void
}) {
  const current = shared(rows, (row) => row.priority)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <BarButton icon={<Flag />} disabled={disabled}>
          Priority
        </BarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-44">
        {PRIORITIES.map((priority) => (
          <DropdownMenuItem key={priority} onSelect={() => onPick(priority)}>
            <span className="flex-1">{statusLabel(priority)}</span>
            {priority === current && <Check aria-label="Current" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface BatchActionsProps<T extends { id: string }> {
  selection: RowSelection<T>
  edits: OptimisticRows<T>
  people: ReadonlyMap<string, HelmUser>
  meId: string | null
}

/** What a saved inquiry changes on its row: quick edit's fields, and its priority. */
function inquiryBatchFields(saved: Partial<Inquiry>): Partial<Inquiry> {
  return {
    ...quickEditedFields(saved as Inquiry),
    ...(saved.priority ? { priority: saved.priority } : {}),
  }
}

export function InquiryBatchActions({
  selection,
  edits,
  people,
  meId,
}: BatchActionsProps<Inquiry>) {
  const rows = selectedRows(selection, edits.rows)
  const [cancelling, setCancelling] = useState(false)
  const busy = rows.some((row) => edits.saving.has(row.id))
  const disabled = busy || rows.length > BATCH_LIMIT
  const stuck = rows.filter((row) => !canCancelInquiry(row))

  async function run(
    entries: Array<BatchEntry<Inquiry>>,
    item: (entry: BatchEntry<Inquiry>) => BatchInquiryItem,
  ) {
    const visible = edits.rows
    const failed = await edits.saveBatch(
      entries.map(({ row, change }) => ({ row, change: change as Partial<Inquiry> })),
      () => apiClient.batchInquiries(entries.map(item)),
      inquiryTitle,
      inquiryBatchFields,
    )
    selection.keep(failed, visible)
  }

  const update = (entry: BatchEntry<Inquiry>): BatchInquiryItem => ({
    id: entry.row.id,
    update: {
      ...inquiryQuickPatch(entry.change),
      ...(entry.change.priority ? { priority: entry.change.priority } : {}),
    },
  })

  return (
    <BatchDock edits={edits} count={rows.length} noun="inquiries" onClear={selection.clear}>
      <StatusMenu
        rows={rows}
        choicesFor={inquiryStatusChoices}
        order={INQUIRY_STATUSES}
        noun="inquiry"
        disabled={disabled}
        onPick={(status) => void run(statusEntries(rows, status, inquiryStatusChoices), update)}
      />
      <OwnerMenu
        rows={rows}
        people={people}
        meId={meId}
        disabled={disabled}
        onPick={(owner) => void run(ownerEntries(rows, owner), update)}
      />
      <DueDateAction
        rows={rows}
        noun="inquiries"
        disabled={disabled}
        onPick={(date) => void run(dueDateEntries(rows, date), update)}
      />
      <PriorityMenu
        rows={rows}
        disabled={disabled}
        onPick={(priority) => void run(priorityEntries(rows, priority), update)}
      />
      {stuck.length === 0 ? (
        <BarButton
          icon={<Ban />}
          className="text-destructive hover:text-destructive"
          disabled={disabled}
          onClick={() => setCancelling(true)}
        >
          Cancel…
        </BarButton>
      ) : (
        // Says why, rather than sitting greyed out with no reason a phone can show.
        <Popover>
          <PopoverTrigger asChild>
            <BarButton icon={<Ban />} className="text-muted-foreground" disabled={disabled}>
              Cancel…
            </BarButton>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="w-80 space-y-2 text-sm">
            <p>
              Only open inquiries that are not yet a consignment can be cancelled. {stuck.length}{' '}
              selected can't be: {stuck.slice(0, 3).map(inquiryTitle).join('; ')}
              {stuck.length > 3 ? `; and ${stuck.length - 3} more` : ''}.
            </p>
            <p className="text-muted-foreground">Select only the others to cancel them.</p>
          </PopoverContent>
        </Popover>
      )}
      {cancelling && (
        <CancelInquiryDialog
          open
          onOpenChange={(open) => !open && setCancelling(false)}
          inquiry={rows.length === 1 ? rows[0]! : { tasks: [] }}
          label={rows.length === 1 ? inquiryTitle(rows[0]!) : `${rows.length} selected inquiries`}
          count={rows.length}
          onConfirm={async (reason) => {
            // Shown on the rows at once; any Helm refuses go back, with why.
            void run(
              rows.map((row) => ({
                row,
                change: { status: 'CANCELLED', operational_state: 'CANCELLED' },
              })),
              (entry) => ({ id: entry.row.id, cancel: { reason } }),
            )
          }}
        />
      )}
    </BatchDock>
  )
}

export function ConsignmentBatchActions({
  selection,
  edits,
  people,
  meId,
}: BatchActionsProps<Consignment>) {
  const rows = selectedRows(selection, edits.rows)
  const busy = rows.some((row) => edits.saving.has(row.id))
  const disabled = busy || rows.length > BATCH_LIMIT

  async function run(entries: Array<BatchEntry<Consignment>>) {
    const visible = edits.rows
    const failed = await edits.saveBatch(
      entries.map(({ row, change }) => ({ row, change: change as Partial<Consignment> })),
      () =>
        apiClient.batchConsignments(
          entries.map(({ row, change }) => ({ id: row.id, update: consignmentQuickPatch(change) })),
        ),
      consignmentLabel,
      (saved) => quickEditedFields(saved as Consignment),
    )
    selection.keep(failed, visible)
  }

  return (
    <BatchDock edits={edits} count={rows.length} noun="consignments" onClear={selection.clear}>
      <StatusMenu
        rows={rows}
        choicesFor={consignmentStatusChoices}
        order={CONSIGNMENT_STATUSES}
        noun="consignment"
        disabled={disabled}
        onPick={(status) => void run(statusEntries(rows, status, consignmentStatusChoices))}
      />
      <OwnerMenu
        rows={rows}
        people={people}
        meId={meId}
        disabled={disabled}
        onPick={(owner) => void run(ownerEntries(rows, owner))}
      />
      <DueDateAction
        rows={rows}
        noun="consignments"
        disabled={disabled}
        onPick={(date) => void run(dueDateEntries(rows, date))}
      />
    </BatchDock>
  )
}
